import type { AttachmentKind } from '../api/types'

export const MB = 1024 * 1024
export const MAX_FILE_BYTES = 25 * MB
export const MAX_IMAGE_BYTES = 15 * MB
export const MAX_VIDEO_BYTES = 200 * MB
export const MAX_AUDIO_BYTES = 25 * MB
export const MAX_ATTACHMENTS = 10
export const MAX_BODY_CHARS = 4000

export const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'] as const

/** Each type that can be sent by its extensions, the one used for a nameless file first. */
const EXTENSION_TYPES: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
  m4a: 'audio/mp4',
  mp3: 'audio/mpeg',
  aac: 'audio/aac',
  weba: 'audio/webm',
  ogg: 'audio/ogg',
  oga: 'audio/ogg',
  opus: 'audio/ogg',
  wav: 'audio/wav',
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  doc: 'application/msword',
  xls: 'application/vnd.ms-excel',
  txt: 'text/plain',
  csv: 'text/csv',
  zip: 'application/zip',
}

/** Other names devices give types that can be sent, as the server stores them (contract: "Attachments"). */
const TYPE_ALIASES: Record<string, string> = {
  'audio/x-m4a': 'audio/mp4',
  'audio/x-wav': 'audio/wav',
  'audio/wave': 'audio/wav',
  'video/x-m4v': 'video/mp4',
  'audio/mp3': 'audio/mpeg',
  'audio/x-aac': 'audio/aac',
  'application/x-zip-compressed': 'application/zip',
}

export const ALLOWED_TYPES = new Set(Object.values(EXTENSION_TYPES))

/** What the file picker offers: every type that can be sent, by type and by extension. */
const ACCEPTED = [...ALLOWED_TYPES, ...Object.keys(TYPE_ALIASES), ...Object.keys(EXTENSION_TYPES).map((ext) => `.${ext}`)]
export const ATTACHMENT_ACCEPT = ACCEPTED.join(',')

/** A photo or video, by type or by extension. */
function isMediaEntry(entry: string): boolean {
  const type = entry.startsWith('.') ? EXTENSION_TYPES[entry.slice(1)] : entry
  return type.startsWith('image/') || type.startsWith('video/')
}

/** The attach panel's Photos: the photos and videos that can be sent. */
export const MEDIA_ACCEPT = ACCEPTED.filter(isMediaEntry).join(',')

/** The attach panel's Document: everything else that can be sent. With no photos or videos in it, iOS opens Files directly. */
export const DOCUMENT_ACCEPT = ACCEPTED.filter((entry) => !isMediaEntry(entry)).join(',')

/** The file extension for a type that can be sent, e.g. "jpg" for image/jpeg. */
export function extensionFor(contentType: string): string | undefined {
  const type = normalizeContentType(contentType)
  return Object.entries(EXTENSION_TYPES).find(([, t]) => t === type)?.[0]
}

/** A type without its parameters, under the one name it is sent as: "audio/webm;codecs=opus" is "audio/webm". */
export function normalizeContentType(contentType: string): string {
  const type = contentType.split(';')[0].trim().toLowerCase()
  return TYPE_ALIASES[type] ?? type
}

/** Browsers report some types inconsistently (empty, or csv as ms-excel); prefer the extension. */
export function resolveContentType(fileName: string, reported: string): string {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? ''
  const byExtension = EXTENSION_TYPES[ext]
  const type = normalizeContentType(reported)
  if (byExtension && (!type || !ALLOWED_TYPES.has(type) || ext === 'csv')) return byExtension
  return type || byExtension || 'application/octet-stream'
}

export function isImageType(contentType: string): boolean {
  return (IMAGE_TYPES as readonly string[]).includes(contentType)
}

/** What an attachment is, from its type, as the server works it out. */
export function attachmentKind(contentType: string): AttachmentKind {
  const type = normalizeContentType(contentType)
  if (type.startsWith('image/')) return 'image'
  if (type.startsWith('video/')) return 'video'
  if (type.startsWith('audio/')) return 'audio'
  return 'file'
}

/** Video and audio: converted by the worker and played in the message. */
export function isClipType(contentType: string): boolean {
  const kind = attachmentKind(contentType)
  return kind === 'video' || kind === 'audio'
}

const limits: Record<AttachmentKind, { bytes: number; label: string }> = {
  image: { bytes: MAX_IMAGE_BYTES, label: ' for images' },
  video: { bytes: MAX_VIDEO_BYTES, label: ' for videos' },
  audio: { bytes: MAX_AUDIO_BYTES, label: ' for audio' },
  file: { bytes: MAX_FILE_BYTES, label: '' },
}

export type FileCheck = { ok: true; contentType: string } | { ok: false; reason: string }

export function checkFile(fileName: string, reportedType: string, sizeBytes: number): FileCheck {
  const contentType = resolveContentType(fileName, reportedType)
  if (!ALLOWED_TYPES.has(contentType)) {
    return { ok: false, reason: `${fileName} can't be sent. Use images, videos, audio, PDFs, Office documents, text, CSV or zip files.` }
  }
  const limit = limits[attachmentKind(contentType)]
  if (sizeBytes > limit.bytes) {
    return { ok: false, reason: `${fileName} is too large. The limit is ${limit.bytes / MB} MB${limit.label}.` }
  }
  if (sizeBytes === 0) return { ok: false, reason: `${fileName} is empty.` }
  return { ok: true, contentType }
}
